import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@cfg/env/app.config';
import { UserSignatureRepository } from '@service/signature/user-signature.repository';
import {
  UserSignatureListResponse,
  ToggleSignatureStatusResponse,
  CoordinatorSignerResponse
} from '@model/signature/user-signature.model';
import {
  SignatureDetailResponse,
  UpdateUserDataRequest,
  UpdateUserDataResponse
} from '@model/signature/update-user-signature.model';

/**
 * HTTP implementation of UserSignatureRepository.
 */
@Injectable({
  providedIn: 'root'
})
export class HttpUserSignatureRepository extends UserSignatureRepository {
  private readonly httpClient = inject(HttpClient);
  private readonly appConfig = inject(APP_CONFIG);
  private readonly signaturesPath = '/signatures';

  private get baseUrl(): string {
    const config = this.appConfig.validationResults as
      | { baseUrl?: string; context: string }
      | undefined;
    const base = config?.baseUrl ?? '';
    const context = config?.context ?? '/validacion_resultados';
    return `${base}${context}`;
  }

  override getAll(
    page: number,
    size: number
  ): Observable<UserSignatureListResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.httpClient.get<UserSignatureListResponse>(
      `${this.baseUrl}${this.signaturesPath}`,
      { params }
    );
  }

  override toggleStatus(
    userId: string
  ): Observable<ToggleSignatureStatusResponse> {
    return this.httpClient.put<ToggleSignatureStatusResponse>(
      `${this.baseUrl}${this.signaturesPath}/${userId}/toggle-status`,
      {}
    );
  }

  override toggleCoordinatorSigner(
    userId: string
  ): Observable<CoordinatorSignerResponse> {
    return this.httpClient.put<CoordinatorSignerResponse>(
      `${this.baseUrl}${this.signaturesPath}/${userId}/coordinator-signer`,
      {}
    );
  }

  override getById(
    userId: string
  ): Observable<SignatureDetailResponse> {
    return this.httpClient.get<SignatureDetailResponse>(
      `${this.baseUrl}${this.signaturesPath}/${userId}`
    );
  }

  override updateUserData(
    userId: string,
    request: UpdateUserDataRequest
  ): Observable<UpdateUserDataResponse> {
    return this.httpClient.put<UpdateUserDataResponse>(
      `${this.baseUrl}${this.signaturesPath}/${userId}/update`,
      request
    );
  }
}
